import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';

import type { BylineContentType } from '../utils/contentAri.ts';

import { preloadRelatedContentSample } from './preloadRelatedContentSample.ts';
import {
	createRelatedContentSsrState,
	RELATED_CONTENT_SAMPLE_LIMIT,
	type RelatedContentSampleResult,
	type RelatedContentSsrState,
} from './relatedContentSsrState.ts';

type RelatedContentSsrRoute = 'view' | 'live';

type PreloadRelatedContentSsrStateArgs = Readonly<{
	cloudId: string;
	activationId: string;
	contentId: string;
	/** Pages and blog posts are distinct graph node types. Undefined means page. */
	contentType?: BylineContentType;
	route: RelatedContentSsrRoute;
}>;

const RELATED_CONTENT_SAMPLE_OUTCOME_MEASURE = 'ssr-app/prepare/relatedContentSample';

const recordRelatedContentSampleOutcome = (
	result: RelatedContentSampleResult,
	route: RelatedContentSsrRoute,
): void => {
	let outcome: string;
	if (result.status === 'unavailable') {
		outcome = result.reason;
	} else if (result.count === 0) {
		outcome = 'empty';
	} else if (result.count >= RELATED_CONTENT_SAMPLE_LIMIT) {
		outcome = 'limit-reached';
	} else {
		outcome = 'one-or-two';
	}

	SSRMeasures.addMeasure(
		`${RELATED_CONTENT_SAMPLE_OUTCOME_MEASURE}/${route}/outcome:${outcome}`,
		0,
	);
};

const createStateFromSample = (
	contentId: string,
	result: RelatedContentSampleResult,
): RelatedContentSsrState => {
	if (result.status === 'unavailable') {
		return createRelatedContentSsrState(contentId, 'unavailable');
	}
	return result.count > 0
		? createRelatedContentSsrState(contentId, 'present', result.count)
		: createRelatedContentSsrState(contentId, 'empty');
};

// Runs alongside the outbound and inbound preloads in the route's main tasks, under the same
// slow-query guard: a slow graph answer degrades to `unavailable` rather than holding the render.
export const preloadRelatedContentSsrState = async ({
	cloudId,
	activationId,
	contentId,
	contentType,
	route,
}: PreloadRelatedContentSsrStateArgs): Promise<RelatedContentSsrState> => {
	let samplePromise: Promise<RelatedContentSampleResult> | undefined;
	const getSample = (): Promise<RelatedContentSampleResult> => {
		samplePromise ??= preloadRelatedContentSample({
			cloudId,
			activationId,
			contentId,
			contentType,
		});
		return samplePromise;
	};
	const completedWithinGuard = await slowQueryGuard(
		'preloadRelatedContentSample',
		async () => {
			await getSample();
		},
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);

	if (!completedWithinGuard) {
		SSRMeasures.addMeasure(`${RELATED_CONTENT_SAMPLE_OUTCOME_MEASURE}/${route}/outcome:timeout`, 0);
		return createRelatedContentSsrState(contentId, 'unavailable');
	}

	const sample = await getSample();
	recordRelatedContentSampleOutcome(sample, route);
	return createStateFromSample(contentId, sample);
};
